import React, { useCallback, useEffect } from "react";
import { useFlow } from "@/contexts/FlowContext";
import { useFrame } from "@/contexts/FrameContext";
import { useModel } from "@/contexts/ModelContext";
import { useGetPredictions } from "@/hooks/useGetPredictions";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useCurrentMedia, useSyncPromptLabels, useTextPromptMode } from "@/store";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { GeneratePredictionsBtn } from "./GeneratePredictionsBtn";
import { TextPrompt } from "./TextPrompt";
import { TextPromptProposalChips } from "./TextPromptProposalChips";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { ErrorInfo } from "@/components/ErrorInfo";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { getAutoInvokeStatus, setAutoInvokeStatus } from "@/utils/autoInvokeStatus";
import _ from "lodash";
import { useIsGeneratingPredictions } from "@/hooks/useIsGeneratingPredictions";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

interface GeneratePredictionsProps {
    disabled?: boolean;
    hideTextPrompt?: boolean;
}

export const GeneratePredictions = ({
    disabled = false,
    hideTextPrompt = false
}: GeneratePredictionsProps) => {
    const { setFlowLocked } = useFlow();
    const { getPredictionsForFile } = useGetPredictions();
    const currentMedia = useCurrentMedia();
    const { setAnnotationsForFileAndFrameId, textPrompts, promptLabels, setTextPromptStr } =
        useModel();
    const { currentFramePredictions, currentFrameAnnotations } = useComputedValues();
    const syncPromptLabels = useSyncPromptLabels();
    const { track } = useAnalytics();
    const textPromptMode = useTextPromptMode();
    const showTextPrompt = !hideTextPrompt || textPromptMode;

    // Use the auto-generate predictions hook
    // const { isAutoGenerating } = useAutoPredictions();

    // Check if there are any drawn annotations
    const hasDrawnAnnotations = currentFrameAnnotations.length > 0;

    // Check if there are any draft annotations
    const hasDraftAnnotations = currentFrameAnnotations.some((anno) => anno.draft);

    // Check if there are object cards
    const hasObjectCards = promptLabels.size > 0;

    const markAnnotationsAsDraft = useCallback(
        (fileId: string, frameId: string) => {
            setAnnotationsForFileAndFrameId((prev) => {
                const current = prev[fileId] || {};
                current[frameId] =
                    current[frameId]?.map((anno) => ({ ...anno, draft: true })) || [];
                return { ...prev, [fileId]: current };
            });
        },
        [setAnnotationsForFileAndFrameId]
    );

    const handleBoxPrompt = useCallback(async () => {
        if (!(currentMedia?.file && currentMedia?.frame)) return;

        setFlowLocked(true);

        // Note: Box drawing analytics are tracked in AnnotationCanvas when user actually draws a box

        try {
            await new Promise<void>(async (resolve, reject) => {
                try {
                    if (currentMedia?.file.isVideo) {
                        await getPredictionsForFile({
                            mediaItem: currentMedia?.frame!,
                            onProgress: (progress) => {
                                if (progress === 100) {
                                    markAnnotationsAsDraft(
                                        currentMedia?.file.id,
                                        currentMedia?.frame?.id!
                                    );
                                    resolve();
                                }
                            },
                            extractedFrameMeta: {
                                videoId: currentMedia?.file.id,
                                segment: currentMedia?.frame?.metadata?.segment!
                            },
                            isDraft: true
                        });
                    } else {
                        await getPredictionsForFile({
                            mediaItem: currentMedia?.file!,
                            onProgress: (progress) => {
                                if (progress === 100) {
                                    markAnnotationsAsDraft(
                                        currentMedia?.file.id,
                                        currentMedia?.frame?.id!
                                    );
                                    resolve();
                                }
                            },
                            isDraft: true
                        });
                    }
                } catch (error) {
                    reject(error);
                }
            });
            // on success, keep locked (PromptSidebarContent will manage unlocking based on predictions)
        } catch (e) {
            console.error(e);
            // On error, unlock so user can retry
            setFlowLocked(false);
            throw e;
        } finally {
        }
    }, [currentMedia, setFlowLocked, getPredictionsForFile, markAnnotationsAsDraft]);

    // Text prompt handler (copied from TextPrompt.tsx)
    const handleGeneratePredictions = useCallback(async () => {
        setFlowLocked(true);

        const combinedTextPrompts = textPromptMode
            ? _.uniq([
                  ...textPrompts,
                  ...Array.from(promptLabels.values()).map((label) => label.name)
              ])
            : textPrompts;

        syncPromptLabels(combinedTextPrompts);

        try {
            await new Promise<void>(async (resolve, reject) => {
                if (!currentMedia?.file.metadata?.naturalDimensions || !currentMedia?.frame) {
                    resolve();
                    return;
                }

                try {
                    if (currentMedia?.file.isVideo) {
                        if (typeof currentMedia?.frame?.metadata?.segment !== "number") {
                            resolve();
                            return;
                        }

                        await getPredictionsForFile({
                            mediaItem: currentMedia?.frame!,
                            onProgress: (progress) => {
                                if (progress === 100 && currentMedia?.frame?.id) {
                                    // mark as draft
                                    setAnnotationsForFileAndFrameId((prev) => {
                                        const current = prev[currentMedia?.file.id] || {};
                                        current[currentMedia?.frame?.id!] =
                                            current[currentMedia?.frame?.id!]?.map((anno) => {
                                                return {
                                                    ...anno,
                                                    draft: textPromptMode
                                                        ? false
                                                        : combinedTextPrompts.includes(anno.label)
                                                };
                                            }) || [];
                                        return { ...prev, [currentMedia?.file.id]: current };
                                    });

                                    resolve();
                                }
                            },
                            extractedFrameMeta: {
                                videoId: currentMedia?.file.id,
                                segment: currentMedia?.frame?.metadata?.segment!
                            },
                            inferenceType: InferenceTypes.SEG_PREVIEW,
                            textPrompts: combinedTextPrompts,
                            isDraft: true
                        });
                    } else {
                        await getPredictionsForFile({
                            mediaItem: currentMedia?.file!,
                            onProgress: (progress) => {
                                if (progress === 100) {
                                    // mark as draft
                                    setAnnotationsForFileAndFrameId((prev) => {
                                        const current = prev[currentMedia?.file.id] || {};
                                        current[currentMedia?.file.id] =
                                            current[currentMedia?.file.id]?.map((anno) => {
                                                return {
                                                    ...anno,
                                                    draft: textPromptMode
                                                        ? false
                                                        : combinedTextPrompts.includes(anno.label)
                                                };
                                            }) || [];
                                        return { ...prev, [currentMedia?.file.id]: current };
                                    });
                                    resolve();
                                }
                            },
                            inferenceType: InferenceTypes.SEG_PREVIEW,
                            textPrompts: combinedTextPrompts,
                            isDraft: true
                        });
                    }
                    if (textPromptMode) {
                        setTextPromptStr("");
                        // No approve phase, so unlock immediately
                        setFlowLocked(false);
                    }
                } catch (error) {
                    reject(error);
                }
            });
            // on success, keep locked (PromptSidebarContent will manage unlocking based on predictions)
        } catch (e) {
            console.error(e);
            // On error, unlock so user can retry
            setFlowLocked(false);
            throw e;
        }
    }, [
        currentMedia,
        getPredictionsForFile,
        textPrompts,
        setFlowLocked,
        syncPromptLabels,
        setAnnotationsForFileAndFrameId,
        promptLabels,
        textPromptMode,
        setTextPromptStr
    ]);

    // Smart click handler
    const handleSmartClickSwitcher = useCallback(async () => {
        if (hasDrawnAnnotations && !textPromptMode) {
            // If there are drawn annotations, use box prompt
            await handleBoxPrompt();
        } else if (textPrompts.length > 0) {
            // If there are text prompts, use text prompt generation
            await handleGeneratePredictions();
        } else if (hasObjectCards) {
            // If there are object cards but no text prompts, use text prompt generation with existing labels
            await handleGeneratePredictions();
        }
    }, [
        handleBoxPrompt,
        handleGeneratePredictions,
        hasDrawnAnnotations,
        hasObjectCards,
        textPrompts.length,
        textPromptMode
    ]);

    const {
        error: errorGeneratePredictions,
        reset: resetGeneratePredictions,
        invoke: invokeGeneratePredictions
    } = useAsyncBoundOperation(handleSmartClickSwitcher);

    // Combine loading states - either manual generation or auto-generation
    const isGeneratingPredictions = useIsGeneratingPredictions();

    // Auto-invoke predictions only once when first entering the build step
    useEffect(() => {
        const shouldAutoInvoke =
            !getAutoInvokeStatus() &&
            textPrompts.length > 0 &&
            currentFramePredictions.length === 0 &&
            !isGeneratingPredictions &&
            !hasDrawnAnnotations &&
            !hasDraftAnnotations && // Don't auto-invoke if there are draft annotations (predictions were already generated)
            currentMedia?.file.metadata?.naturalDimensions &&
            currentMedia?.frame;

        if (shouldAutoInvoke) {
            setAutoInvokeStatus(true);
            invokeGeneratePredictions();
        }
    }, [
        textPrompts.length,
        currentFramePredictions.length,
        isGeneratingPredictions,
        hasDrawnAnnotations,
        hasDraftAnnotations,
        currentMedia?.file.metadata?.naturalDimensions,
        currentMedia?.frame,
        invokeGeneratePredictions
    ]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            // Track generate predictions button click
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "button_click",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
            e.preventDefault();
        },
        [
            invokeGeneratePredictions,
            track,
            hasDrawnAnnotations,
            textPrompts,
            hasObjectCards,
            currentFrameAnnotations.length
        ]
    );

    // Form submit handler
    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            // Track generate predictions form submit
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "form_submit",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
            e.preventDefault();
        },
        [
            invokeGeneratePredictions,
            track,
            hasDrawnAnnotations,
            textPrompts,
            hasObjectCards,
            currentFrameAnnotations.length
        ]
    );

    const handleRetry = useCallback(
        (e: React.MouseEvent) => {
            resetGeneratePredictions();
            invokeGeneratePredictions();
            e.preventDefault();
        },
        [invokeGeneratePredictions, resetGeneratePredictions]
    );

    // Keyboard shortcut: Cmd+Enter to generate predictions
    useKeyboardShortcut({
        key: "Enter",
        handler: () => {
            // Track generate predictions keyboard shortcut
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "keyboard_shortcut",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
        },
        requireMeta: true
    });

    // Note: Auto-generation on frame change is now handled by useAutoPredictions hook

    // Determine if button should be disabled
    const isDisabled =
        isGeneratingPredictions ||
        disabled ||
        (!textPrompts.length && !hasObjectCards) ||
        (textPrompts.length > 0 && currentFramePredictions.length > 0);
    // Note: We no longer disable during upload since auto-predict handles race conditions

    const showTextPromptProposalChips = !hasObjectCards || textPromptMode;

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="prompt-container">
                {showTextPrompt && <TextPrompt />}
                <GeneratePredictionsBtn disabled={isDisabled} onClick={handleClick} />
                {errorGeneratePredictions && (
                    <ErrorInfo error={errorGeneratePredictions} onRetryClick={handleRetry} />
                )}
            </div>
            {showTextPrompt && showTextPromptProposalChips && (
                <TextPromptProposalChips
                    isDisabled={isGeneratingPredictions}
                    showPoweredBy={false}
                />
            )}
        </form>
    );
};
